import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, PageLoader } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RiskBadge } from '@/components/common/RiskBadge';
import { CategoryBadge } from '@/components/common/CategoryBadge';
import { EvidenceBadge } from '@/components/common/EvidenceBadge';
import { ObservationStageBadge } from '@/components/common/ObservationStageBadge';
import { SimulationDisclaimer } from '@/components/common/SimulationDisclaimer';
import { VerifiedBadge } from '@/components/common/VerifiedBadge';
import { BeforeAfterViewer } from '@/components/detection/BeforeAfterViewer';
import { api, downloadPdf } from '@/lib/api';
import { fmtDate, fmtCoord } from '@/lib/format';
import { fmtDistance } from '@/lib/format2';
import { canValidate, useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { FileText, ArrowRight, Info } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

const ALERT_STATUSES = ['New', 'Under review', 'Validated', 'Rejected', 'Escalated', 'Closed', 'Archived'];

export default function AlertDetailPage() {
  const { alertId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [alert, setAlert] = useState(null);
  const [detection, setDetection] = useState(null);
  const [asset, setAsset] = useState(null);
  const [baseObs, setBaseObs] = useState(null);
  const [newObs, setNewObs] = useState(null);
  usePageTitle(alert ? `Alert · ${alert.title.slice(0, 40)}` : 'Alert');

  const load = async () => {
    const a = await api.get(`/alerts/${alertId}`).then(r => r.data);
    setAlert(a);
    const d = await api.get(`/detections/${a.detection_id}`).then(r => r.data);
    setDetection(d);
    const [as, b, n] = await Promise.all([
      api.get(`/assets/${a.asset_id}`).then(r => r.data),
      api.get(`/observations/${d.baseline_observation_id}`, { params: { include_image: true } }).then(r => r.data),
      api.get(`/observations/${d.new_observation_id}`, { params: { include_image: true } }).then(r => r.data),
    ]);
    setAsset(as); setBaseObs(b); setNewObs(n);
  };
  useEffect(() => { load(); }, [alertId]);

  const changeStatus = async (status) => {
    await api.patch(`/alerts/${alertId}/status`, { status });
    toast.success(`Alert marked ${status}`);
    load();
  };

  const convertToIncident = async () => {
    const { data } = await api.post('/incidents', { alert_id: alertId, description: detection?.description });
    toast.success(`Incident ${data.number} created`);
    nav(`/app/incidents/${data.id}`);
  };

  if (!alert || !detection || !asset) return <div className="p-6"><PageLoader /></div>;

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <PageHeader
        title={alert.title}
        subtitle={<span className="flex items-center gap-2"><Link to={`/app/assets/${asset.id}`} className="hover:text-primary">{asset.name}</Link>{alert.is_simulation ? <SimulationDisclaimer compact /> : <VerifiedBadge />}</span>}
        actions={
          <div className="flex items-center gap-2">
            <CategoryBadge category={alert.category} />
            <RiskBadge severity={alert.severity} testId="alert-risk-badge" />
            <Button
              variant="outline"
              data-testid="alert-report-btn"
              onClick={() => downloadPdf(`/reports/alert/${alertId}/pdf`, `AakashDrishti_Alert_${alertId.slice(0, 8)}.pdf`)}
            >
              <FileText size={14} className="mr-1" /> PDF report
            </Button>
          </div>
        }
      />

      {alert.is_simulation ? <div className="mb-4"><SimulationDisclaimer /></div> : <div className="mb-4"><VerifiedBadge variant="full" /></div>}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {baseObs?.image_b64 && newObs?.image_b64 && (
            <BeforeAfterViewer
              before={baseObs.image_b64}
              after={newObs.image_b64}
              overlayB64={detection.overlay_jpg_b64}
              meta={{ baseline: baseObs.acquired_at, after: newObs.acquired_at, source: newObs.data_source, resolution: newObs.spatial_resolution_m }}
              detection={detection}
            />
          )}

          <Card className="p-4">
            <div className="text-sm font-semibold">Interpretation</div>
            <p className="mt-2 text-sm">{detection.description}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(detection.affected_features || []).map(f => <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>)}
            </div>
            {detection.recommended_action && (
              <div className="mt-3 text-xs"><b>Recommended action:</b> <span className="text-muted-foreground">{detection.recommended_action}</span></div>
            )}
            <div className="mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground flex items-start gap-1.5">
              <Info size={12} className="shrink-0 mt-0.5" />
              <span>All alerts are AI-generated observations qualified by analyst review. Confidence relates to AI interpretation certainty, not incident probability. Field verification may be required.</span>
            </div>
          </Card>

          {(detection.risk_factors || []).length > 0 && (
            <Card className="p-4">
              <div className="text-sm font-semibold">Risk factors</div>
              <div className="mt-2 text-[11px] text-muted-foreground">Contributing factors to the risk score:</div>
              <div className="mt-2 divide-y divide-border">
                {detection.risk_factors.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 py-1.5 text-xs">
                    <span className="flex-1">{f.name}</span>
                    <span className="font-mono tabular-nums text-muted-foreground">{f.display}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="p-4">
            <div className="text-sm font-semibold">Alert summary</div>
            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
              <Row k="Status" v={<Badge variant="outline" className="text-[10px]">{alert.status}</Badge>} />
              <Row k="Risk" v={<span className="font-mono">{alert.risk_score}/100</span>} />
              <Row k="AI confidence" v={<span className="font-mono">{Math.round((alert.confidence || 0) * 100)}%</span>} />
              <Row k="Evidence" v={<EvidenceBadge quality={alert.evidence_quality} />} />
              <Row k="Location" v={<span className="font-mono">{fmtCoord(alert.change_lat)}, {fmtCoord(alert.change_lng)}</span>} />
              <Row k="Distance to asset" v={<span className="font-mono">{fmtDistance(alert.distance_to_asset_m)}</span>} />
              <Row k="Created" v={fmtDate(alert.created_at)} />
            </div>

            {canValidate(user.role) && (
              <div className="mt-4 pt-3 border-t border-border space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Workflow</div>
                <Select value={alert.status} onValueChange={changeStatus}>
                  <SelectTrigger data-testid="alert-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ALERT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!alert.incident_id && (
                  <Button size="sm" className="w-full" onClick={convertToIncident} data-testid="convert-to-incident-btn">
                    <ArrowRight size={14} className="mr-1" /> Convert to incident
                  </Button>
                )}
                {alert.incident_id && (
                  <Link to={`/app/incidents/${alert.incident_id}`}>
                    <Button size="sm" variant="outline" className="w-full">View linked incident</Button>
                  </Link>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) { return (<><div className="text-muted-foreground">{k}</div><div className="text-right">{v}</div></>); }
